import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Button from 'components/Button/Button';
import { cn } from 'components/lib/utils';
import { Popover } from 'components/Popover';

import { AutocompleteCheckList } from '../AutocompleteCheckList/AutocompleteCheckList';
import { CheckList } from '../CheckList/CheckList';
import { getChecklistSelectionChanges } from '../CheckList/checklistSelectionChanges';
import { DatePickerFilter } from '../DatePickerFilter/DatePickerFilter';
import { FreeSearch } from '../FreeSearch/FreeSearch';
import { SelectFilter } from '../SelectFilter/SelectFilter';
import { Trigger } from '../Trigger/Trigger';
import { TriggerState } from '../Trigger/types';

import { FilterPopoverProps, FilterType } from './types';

const CHECKLIST_POPOVER_WIDTH = 200;
const AUTOCOMPLETE_CHECKLIST_POPOVER_WIDTH = 300;
const DEFAULT_CHECKLIST_HEIGHT = 300;
const CHECKLIST_FOOTER_BUTTON_CLASSNAME =
  'flex h-7 flex-1 shrink-0 basis-0 items-center justify-center gap-2 rounded-md px-3 py-2';

const getPopoverDimensions = (
  filterType: FilterType,
  popoverHeight?: string
) => {
  switch (filterType) {
    case FilterType.CHECKLIST:
      return {
        width: `${CHECKLIST_POPOVER_WIDTH}px`,
        height: popoverHeight ?? `${DEFAULT_CHECKLIST_HEIGHT}px`
      };
    case FilterType.AUTOCOMPLETE_CHECKLIST:
      return {
        width: `${AUTOCOMPLETE_CHECKLIST_POPOVER_WIDTH}px`,
        height: popoverHeight ?? 'auto'
      };
    default:
      return {};
  }
};

export const FilterPopover = ({
  options,
  selectedItems,
  selectionCount,
  onChange,
  onSelectionChange,
  label,
  labelParts,
  filterType,
  placeholder,
  hideIndicator,
  dataTestId,
  popoverHeight,
  isLocalTime,
  singleSelect = false,
  minLength,
  maxLength
}: FilterPopoverProps) => {
  const triggerSelectionCount = selectionCount ?? selectedItems?.length ?? 0;
  const filterPopoverRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [draftSelectedItems, setDraftSelectedItems] = useState<string[]>([]);

  const handleOpen = useCallback((): void => {
    setDraftSelectedItems(selectedItems ?? []);
    setIsOpen(true);
  }, [selectedItems]);

  const handleDraftToggle = useCallback(
    (value: string): void => {
      setDraftSelectedItems((current: string[]): string[] => {
        if (current.includes(value)) {
          return current.filter((item: string): boolean => item !== value);
        }
        return singleSelect ? [value] : [...current, value];
      });
    },
    [singleSelect]
  );

  const commitChecklistSelection = useCallback(
    (targetSelection: string[]): void => {
      if (onSelectionChange) {
        onSelectionChange(targetSelection);
      } else if (onChange) {
        const appliedItems = selectedItems ?? [];
        for (const value of getChecklistSelectionChanges(
          appliedItems,
          targetSelection
        )) {
          onChange(value);
        }
      }
      setIsOpen(false);
    },
    [onChange, onSelectionChange, selectedItems]
  );

  const handleResetDraft = useCallback((): void => {
    setDraftSelectedItems([]);
    commitChecklistSelection([]);
  }, [commitChecklistSelection]);

  const handleApplyDraft = useCallback((): void => {
    commitChecklistSelection(draftSelectedItems);
  }, [commitChecklistSelection, draftSelectedItems]);

  const handleApply = useCallback(
    (value: string) => {
      onChange?.(value);
      setIsOpen(false);
    },
    [onChange]
  );

  const handleSelectChange = useCallback(
    (value: string) => {
      onChange?.(value);
    },
    [onChange]
  );

  const showCount = useMemo(() => {
    return (
      selectedItems &&
      selectedItems.length > 0 &&
      (filterType === FilterType.CHECKLIST ||
        filterType === FilterType.AUTOCOMPLETE_CHECKLIST)
    );
  }, [selectedItems, filterType]);

  useEffect(() => {
    if (
      !isOpen ||
      filterType === FilterType.DATE_PICKER ||
      filterType === FilterType.SELECT
    )
      return;

    const listener = (event: MouseEvent) => {
      if (filterPopoverRef.current?.contains(event.target as Node)) {
        return;
      }
      setIsOpen(false);
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('click', listener);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', listener);
    };
  }, [isOpen, filterType]);

  const renderChecklistFooter = (): JSX.Element => (
    <div className="flex items-center gap-2 px-2 pb-2 pt-[12px]">
      <Button
        type="button"
        onClick={handleApplyDraft}
        dataTestId={dataTestId ? `${dataTestId}-apply` : undefined}
        className={cn(
          CHECKLIST_FOOTER_BUTTON_CLASSNAME,
          'bg-[#4F46E5] text-white hover:bg-indigo-700'
        )}
      >
        Apply
      </Button>
      <Button
        type="button"
        variant="ghost"
        onClick={handleResetDraft}
        dataTestId={dataTestId ? `${dataTestId}-reset` : undefined}
        className={cn(
          CHECKLIST_FOOTER_BUTTON_CLASSNAME,
          'bg-transparent hover:bg-base-accent'
        )}
      >
        Reset
      </Button>
    </div>
  );

  const renderContent = () => {
    switch (filterType) {
      case FilterType.CHECKLIST:
        return (
          <>
            <CheckList
              items={options ?? []}
              selectedItems={draftSelectedItems}
              onChange={handleDraftToggle}
            />
            {renderChecklistFooter()}
          </>
        );
      case FilterType.AUTOCOMPLETE_CHECKLIST:
        return (
          <>
            <AutocompleteCheckList
              items={options ?? []}
              selectedItems={draftSelectedItems}
              onChange={handleDraftToggle}
              placeholder={placeholder}
              dataTestId={dataTestId}
            />
            {renderChecklistFooter()}
          </>
        );
      case FilterType.FREE_SEARCH:
        return (
          <FreeSearch
            label={label}
            onApply={handleApply}
            initialValue={selectedItems?.[0] || ''}
            minLength={minLength}
            maxLength={maxLength}
          />
        );
      case FilterType.DATE_PICKER:
        return (
          <DatePickerFilter
            label={label}
            onApply={handleApply}
            initialValue={selectedItems?.[0] || ''}
            isLocalTime={isLocalTime}
          />
        );
      case FilterType.SELECT:
        return (
          <SelectFilter
            items={options ?? []}
            selectedItems={selectedItems ?? []}
            onChange={handleSelectChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div ref={filterPopoverRef} className="relative" data-testid={dataTestId}>
      <Popover
        {...getPopoverDimensions(filterType, popoverHeight)}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        trigger={
          <Trigger
            showCount={showCount}
            onClick={handleOpen}
            count={triggerSelectionCount}
            state={
              selectedItems && selectedItems.length > 0
                ? TriggerState.FILLED
                : TriggerState.EMPTY
            }
            label={label}
            labelParts={labelParts}
            isActive={isOpen}
            hideIndicator={hideIndicator}
          />
        }
      >
        {renderContent()}
      </Popover>
    </div>
  );
};
